/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: FeatureList.
 * SCOPE: Grouped feature list — Очередь, Бэклог, Архив; DnD within groups.
 * DEPENDS: M-FE-API, featureListGroups
 * LINKS: M-FE-FEATURES, UC-020
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * FeatureList - Группы Очередь / Бэклог / Архив с DnD внутри групп.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - UC-020: группы Бэклог/Архив, showArchived.
 * LAST_CHANGE: 2026-07-14 - UX: заголовок очереди, подсказка drag, направляющее пустое состояние.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import FeatureItem from './FeatureItem';
import { mergeGroupReorder, partitionFeatureGroups } from '../featureListGroups';

function GroupHeader({ title, caption }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: '0.1em',
          lineHeight: 1.4,
        }}
      >
        {title}
      </Typography>
      {caption ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

/* START_METHOD_SortableGroup */
/* START_CONTRACT: SortableGroup
 * PURPOSE: Одна сортируемая группа задач с заголовком.
 * INPUTS: { title, caption, features, groupKey, groups, onReorder, onEdit, onArchive, onDelete, sortable }.
 * OUTPUTS: { JSX.Element|null }.
 * SIDE_EFFECTS: onReorder через mergeGroupReorder.
 * LINKS: UC-020
 * END_CONTRACT: SortableGroup
 */
function SortableGroup({
  title,
  caption,
  features,
  groupKey,
  groups,
  onReorder,
  onEdit,
  onArchive,
  onDelete,
  sortable = true,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!features.length) {
    return null;
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = features.findIndex((feature) => feature.id === active.id);
    const newIndex = features.findIndex((feature) => feature.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedGroup = arrayMove(features, oldIndex, newIndex);
    onReorder(mergeGroupReorder(groups, groupKey, reorderedGroup));
  };

  const list = (
    <>
      {features.map((feature, index) => (
        <FeatureItem
          key={feature.id}
          feature={feature}
          position={index + 1}
          onEdit={onEdit}
          onArchive={onArchive}
          onDelete={onDelete}
          sortable={sortable}
          isLast={index === features.length - 1}
        />
      ))}
    </>
  );

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 2, '&:last-of-type': { mb: 0 } }}>
      <GroupHeader title={title} caption={caption} />
      {sortable ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={features.map((feature) => feature.id)}
            strategy={verticalListSortingStrategy}
          >
            {list}
          </SortableContext>
        </DndContext>
      ) : (
        list
      )}
    </Paper>
  );
}
/* END_METHOD_SortableGroup */

/* START_METHOD_FeatureList */
/* START_CONTRACT: FeatureList
 * PURPOSE: Отобразить группы Очередь → Бэклог → Архив (архив только при showArchived).
 * INPUTS: { features, showArchived, onEdit, onArchive, onDelete, onReorder }.
 * OUTPUTS: { JSX.Element }.
 * SIDE_EFFECTS: нет (делегирует колбэки).
 * LINKS: M-FE-FEATURES, UC-020
 * END_CONTRACT: FeatureList
 */
export default function FeatureList({
  features,
  showArchived = false,
  onEdit,
  onArchive,
  onDelete,
  onReorder,
}) {
  if (features.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: { xs: 6, sm: 8 },
          px: 3,
          textAlign: 'center',
          backgroundImage: (theme) =>
            `radial-gradient(ellipse 80% 60% at 50% 0%, ${theme.palette.primary.main}08 0%, transparent 70%)`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
          }}
        >
          <AssignmentOutlinedIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Задач пока нет
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
          Создайте первую задачу и задайте оценки по ролям — порядок в списке определяет очередь на таймлайне.
        </Typography>
      </Paper>
    );
  }

  const groups = partitionFeatureGroups(features);
  const visibleArchive = showArchived ? groups.archive : [];
  const hasVisible =
    groups.queue.length > 0 || groups.backlog.length > 0 || visibleArchive.length > 0;

  if (!hasVisible) {
    return (
      <Paper variant="outlined" sx={{ px: 3, py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Нет задач для отображения. Включите «Отображать архивные задачи», чтобы увидеть архив.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {groups.queue.length > 0 ? (
        <SortableGroup
          title="Очередь"
          caption={`${groups.queue.length} · перетащите для сортировки`}
          features={groups.queue}
          groupKey="queue"
          groups={groups}
          onReorder={onReorder}
          onEdit={onEdit}
          onArchive={onArchive}
          onDelete={onDelete}
          sortable
        />
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 2 }}>
          <GroupHeader title="Очередь" caption="пусто" />
          <Box sx={{ px: 2, py: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Нет задач в работе. Переведите задачу из бэклога или создайте новую со статусом «В работе».
            </Typography>
          </Box>
        </Paper>
      )}

      {groups.backlog.length > 0 ? (
        <SortableGroup
          title="Бэклог"
          caption={`${groups.backlog.length} · не на таймлайне`}
          features={groups.backlog}
          groupKey="backlog"
          groups={groups}
          onReorder={onReorder}
          onEdit={onEdit}
          onArchive={onArchive}
          onDelete={onDelete}
          sortable
        />
      ) : null}

      {visibleArchive.length > 0 ? (
        <SortableGroup
          title="Архив"
          caption={`${visibleArchive.length}`}
          features={visibleArchive}
          groupKey="archive"
          groups={groups}
          onReorder={onReorder}
          onEdit={onEdit}
          onArchive={onArchive}
          onDelete={onDelete}
          sortable={false}
        />
      ) : null}
    </Box>
  );
}
/* END_METHOD_FeatureList */
